import { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const Modal = ({
  open = true,
  children,
  dialogClassName = 'w-full max-w-sm rounded-lg bg-white shadow-xl',
  bodyClassName = 'bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4',
  overlayClassName = 'bg-gray-900/75',
  images = false,
  onClose = null,
  closeOnOverlayClick = false,
  closeOnEscape = false,
  verticalAlign = 'center'
}) => {
  useEffect(() => {
    if (!open || !closeOnEscape || typeof onClose !== 'function') {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  return (
    <div
      className={classNames(
        'fixed inset-0 z-50 grid p-4 sm:p-6 touch-pan-y',
        verticalAlign === 'top' ? 'items-start justify-items-center overflow-y-auto' : 'place-items-center',
        { 'pointer-events-none': images }
      )}
      id="modal"
    >
      <button
        type="button"
        aria-label="Close modal"
        className={classNames('absolute inset-0', overlayClassName)}
        onClick={() => {
          if (closeOnOverlayClick && typeof onClose === 'function') {
            onClose();
          }
        }}
      />

      <div
        className={classNames(
          'relative z-10 w-full max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-hidden',
          dialogClassName,
          { 'pointer-events-auto': images }
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <div className={classNames('min-h-0 max-h-full overflow-y-auto', bodyClassName)}>{children}</div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  open: PropTypes.bool,
  children: PropTypes.node,
  dialogClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  onClose: PropTypes.func,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  verticalAlign: PropTypes.oneOf(['center', 'top'])
};

export default Modal;
